<svelte:options
	customElement={{
		tag: 'rf-grid',
		shadow: 'open',
	}}
/>

<script module>
	/**
	 * Import
	 */
	import type { CanvasPanelWebComponent } from '@src/types/canvas-panel.d.ts';

	/**
	 * Types
	 */
	type LayoutsEnum = 'horizontal' | 'vertical' | 'masonry';
	// Masonry strats (first in first out, random, biggest first, smallest first)
	export interface ReframeGridProps {
		id?: string;
		tag?: string;
		columns?: number | null;
		'items-height'?: number | null;
		layout?: LayoutsEnum;
		debug?: boolean;
		responsive?: boolean;
		'no-collection'?: boolean;
	}

	type ReframeGridItem = HTMLElement | CanvasPanelWebComponent;

	type ReframeGridColumn = {
		item: ReframeGridItem;
		bounds: DOMRect;
	};
</script>

<script lang="ts">
	/**
	 * Import
	 */
	import classNames from 'classnames';
	import cssVariables from '@src/utils/cssvariables.ts';
	import { onMount, onDestroy } from 'svelte';
	import { get } from 'svelte/store';
	import { useSlottedElements } from '@src/composables/useSlottedElements';
	import debounced from '@src/utils/debounced';
	import { useCollection } from '@src/composables/useCollection.svelte.ts';
	import { v4 as uuidv4 } from 'uuid';
	import { getComponentNotifications } from '@store/notification-centre.svelte';
	import RfNotificationCentre from '@core/elements/rf-notification-centre/rf-notification-centre.svelte';
	import parseBool from '@src/utils/bool';
	import { emitEvent } from '@src/utils/events';
	/**
	 * Props
	 */
	let {
		id = '',
		tag = 'section',
		columns: columnsProp = null,
		'items-height': itemsHeightProp = null,
		layout = 'horizontal', // Options: 'horizontal', 'vertical', 'masonry'
		debug = false,
		responsive = true,
		'no-collection': noCollection = false,
	}: ReframeGridProps = $props();

	/**
	 * Constants
	 */
	const name = 'rf-grid';
	const componentId = uuidv4();

	/**
	 * Variables
	 */

	let gridHeight = $state(0);
	let observers: ResizeObserver[] = [];
	let gridObserver: ResizeObserver | null = null;
	let cols = 0;

	/**
	 * Composables
	 */

	export const { element, getSlottedElements, forEachSlottedElement } = useSlottedElements();

	export const collection = parseBool(noCollection)
		? undefined
		: useCollection(id, {
				element,
				moduleName: name,
				componentId,
			});

	/**
	 * Derived
	 */
	const collectionNotifs = $derived(getComponentNotifications(componentId));

	// init function
	const initGridItems = (): void => {
		/**
		 * Get the current container element and the slotted elements
		 */
		const currentContainer = get(element);
		if (!currentContainer || !getSlottedElements) return;
		/**
		 * Create a new ResizeObserver instance and observe the container
		 */
		gridObserver = new ResizeObserver(() => {
			setColumnCount();

			const CSSItemsHeight = parseFloat(getComputedStyle(currentContainer).getPropertyValue('--rf-grid-items-height'));
			setItemsHeight(CSSItemsHeight);
		});
		gridObserver.observe(currentContainer);

		const items = getSlottedElements() as ReframeGridItem[];
		items.forEach((item) => {
			/**
			 * Create a new ResizeObserver instance and observe the items
			 */
			const resizeObserver = new ResizeObserver(() => onItemResize(items));
			resizeObserver.observe(item);
			observers.push(resizeObserver);
		});
	};

	export const setItemsHeight = (itemsHeight: number): void => {
		/**
		 * Set the CSS variable `--rf-grid-items-height` to the provided value
		 */
		const currentContainer = get(element);
		if (!currentContainer) return;

		currentContainer.style.setProperty('--rf-grid-items-height', `${isNaN(itemsHeight) ? '' : itemsHeight + 'px'}`);

		forEachSlottedElement((item) => {
			const gridItem = item as ReframeGridItem;
			/**
			 *  If the CSS variable `--rf-grid-items-height` is not a number, set the height of the grid item to an empty string
			 */
			gridItem.style.height = !isNaN(itemsHeight) ? itemsHeight + 'px' : '';
		});
	};

	// Set column count
	export const setColumnCount = (columns?: number): void => {
		/**
		 * Set the CSS variable `--rf-grid-columns` to the provided value
		 */
		const currentContainer = get(element);
		if (!currentContainer) return;
		if (columns) {
			/**
			 * Set the CSS variable `--rf-grid-columns` to the provided value
			 */
			cols = columns;
			currentContainer.style.setProperty('--rf-grid-columns', `${columns}`);
		} else {
			/**
			 * Get the number of columns from the CSS variable `--rf-grid-columns` only if it's not set
			 * through the `columns` prop
			 */
			const CSSColumns = parseFloat(getComputedStyle(currentContainer).getPropertyValue('--rf-grid-columns'));
			cols = CSSColumns;
		}
	};

	// Horizontal layout
	const setHorizontalLayout = (items: ReframeGridItem[], columns: ReframeGridColumn[][]): void => {
		let columnIndex = 0;
		/**
		 * Iterate over each item and add it to the appropriate column
		 */
		items.forEach((item) => {
			const bounds = item.getBoundingClientRect();
			columns[columnIndex % cols].push({ item, bounds });
			columnIndex++;
		});
	};

	// Vertical layout
	const setVerticalLayout = (items: ReframeGridItem[], columns: ReframeGridColumn[][]): void => {
		/**
		 * Calculate the number of items per column
		 */
		const totalItems = items.length;
		const itemsPerColumn = Math.floor(totalItems / cols);
		/**
		 * Calculate the number of items that are left over
		 */
		const rest = totalItems % cols;

		let columnIndex = 0;
		let itemsInColumn = 0;

		/**
		 * Iterate over each item and add it to the appropriate column
		 */
		items.forEach((item) => {
			const bounds = item.getBoundingClientRect();
			columns[columnIndex].push({ item, bounds });

			itemsInColumn++;

			/**
			 * If the number of items in the current column is greater than the number of items per column,
			 * move to the next column
			 * If the number of items in the current column is less than the number of items per column,
			 * add an extra item to the current column
			 *
			 * This is to ensure that the grid items are evenly distributed across the columns
			 */
			const maxItemsInCurrentColumn = itemsPerColumn + (columnIndex < rest ? 1 : 0);
			if (itemsInColumn >= maxItemsInCurrentColumn) {
				columnIndex++;
				itemsInColumn = 0;
			}
		});
	};

	// Masonry layout
	const setMasonryLayout = (items: ReframeGridItem[], columns: ReframeGridColumn[][]): void => {
		/**
		 * Iterate over each item and add it to the appropriate column
		 */
		items.forEach((item) => {
			/**
			 * Get the index of the column with the smallest height to always add the next item to the smallest column
			 */
			const currentColIndex = getSmallestColumnIndex(columns);
			const bounds = item.getBoundingClientRect();
			columns[currentColIndex].push({ item, bounds });
		});
	};

	const getSmallestColumnIndex = (columns: ReframeGridColumn[][]): number => {
		let smallestIndex = 0;
		let smallestHeight = Infinity;

		/**
		 * Iterate over each column and calculate the total height of the items in that column
		 */
		columns.forEach((column, index) => {
			const totalHeight = column.reduce((sum, item) => sum + item.bounds.height, 0);
			/**
			 * If the total height of the items in the current column is smaller than the smallest height,
			 * update the smallest height and smallest index
			 */
			if (totalHeight < smallestHeight) {
				smallestHeight = totalHeight;
				smallestIndex = index;
			}
		});

		return smallestIndex;
	};

	// Distribute items into columns based on layout
	const setLayout = (items: ReframeGridItem[], columns: ReframeGridColumn[][]): void => {
		switch (layout) {
			case 'horizontal':
				setHorizontalLayout(items, columns);
				break;
			case 'vertical':
				setVerticalLayout(items, columns);
				break;
			case 'masonry':
				setMasonryLayout(items, columns);
				break;
		}
	};

	// Position items and calculate grid height
	const setItemsPositioning = (columns: ReframeGridColumn[][]): void => {
		const currentContainer = get(element);
		if (!currentContainer) return;

		/**
		 * Get gaps from CSS variables
		 */
		const rowGap = parseFloat(getComputedStyle(currentContainer).getPropertyValue('--rf-grid-row-gap')) || 0;
		const columnGap = parseFloat(getComputedStyle(currentContainer).getPropertyValue('--rf-grid-col-gap')) || 0;

		gridHeight = 0;
		columns.forEach((column, columnIdx) => {
			/**
			 * Calculate the height of the column
			 */
			let columnHeight = 0;
			column.forEach((el, itemIdx) => {
				/**
				 * Calculate the top position of the item in the column based on the previous items in the column
				 */
				const prevItems = column.slice(0, itemIdx);
				let top = 0;

				prevItems.forEach((prevItem) => {
					top += prevItem.bounds.height;
				});

				top += itemIdx * rowGap;

				/**
				 * Apply positioning styles to the item
				 */
				el.item.style.position = 'absolute';
				el.item.style.width = `calc(100% / ${cols} - ${(columnGap * (cols - 1)) / cols}px)`;
				el.item.style.left = `calc(${columnIdx / cols} * 100% + ${(columnGap * (columnIdx - 1)) / cols}px + ${columnGap / cols}px)`;
				el.item.style.top = `${top}px`;
				el.item.style.visibility = 'visible';

				columnHeight += el.bounds.height + (itemIdx > 0 ? rowGap : 0);
			});

			gridHeight = Math.max(gridHeight, columnHeight);
			emitEvent('rf-page-resize');
		});
	};

	// Resize grid
	const gridResize = (items: ReframeGridItem[]): void => {
		let columns = Array.from({ length: cols }, () => []);
		setLayout(items, columns);
		setItemsPositioning(columns);
	};

	const resizeDebounced = debounced(gridResize, 100);

	const onItemResize = (items: ReframeGridItem[]): void => {
		resizeDebounced(items);
	};

	/**
	 * Watch for changes to the `columns` prop
	 */
	$effect(() => {
		if (columnsProp) {
			setColumnCount(columnsProp);
			initGridItems();
		}
	});

	$effect(() => {
		if (layout) {
			initGridItems();
		}
	});

	/**
	 * Watch for changes to the `items-height` prop
	 */
	$effect(() => {
		if (itemsHeightProp) {
			setItemsHeight(itemsHeightProp);
		}
	});

	/**
	 * Lifecycle
	 */
	onMount(() => {
		initGridItems();
		const currentElement = get(element);
		if (currentElement) {
			const slot = currentElement.querySelector('slot');
			if (slot) {
				slot.addEventListener('slotchange', () => {
					initGridItems();
				});
			}
		}
	});

	onDestroy(() => {
		observers.forEach((observer) => observer.disconnect());
		observers = [];
		gridObserver?.disconnect();
	});
</script>

<slot name="header"></slot>
<svelte:element
	this={tag}
	bind:this={$element}
	part="rf-grid"
	class={classNames('rf-grid', layout, { responsive: parseBool(responsive) })}
	style={cssVariables({
		'--rf-grid-height': gridHeight + 'px',
		'--rf-grid-default-items-height': itemsHeightProp,
		...(columnsProp ? { '--rf-grid-default-columns': columnsProp } : {}),
	})}
>
	{#if collectionNotifs && debug}
		<RfNotificationCentre notifications={collectionNotifs} />
	{/if}
	<div class="rf-grid__wrapper" part="rf-grid__wrapper"><slot></slot></div>
</svelte:element>
<slot name="footer"></slot>

<style lang="scss">
	@use 'assets/scss/core/rf-breakpoints' as breakpoint;

	.rf-grid {
		--rf-grid-row-gap: var(--rf-row-gap, 20px);
		--rf-grid-col-gap: var(--rf-col-gap, 20px);
		--rf-grid-items-height: var(--rf-grid-default-items-height, '');
		--rf-grid-columns: var(--rf-grid-default-columns, 3);
		box-sizing: border-box;
		position: relative;

		&.responsive {
			@container rf-grid (width < #{breakpoint.$m}) {
				--rf-grid-columns: 3;
			}

			@container rf-grid (width < #{breakpoint.$s}) {
				--rf-grid-columns: 2;
				--rf-grid-row-gap: var(--rf-spacing-s, 16px);
				--rf-grid-col-gap: var(--rf-spacing-s, 16px);
			}

			@container rf-grid (width < #{breakpoint.$xs}) {
				--rf-grid-columns: 1;
				--rf-grid-row-gap: var(--rf-spacing-xs, 8px);
				--rf-grid-col-gap: var(--rf-spacing-xs, 8px);
			}
		}

		&__wrapper {
			height: var(--rf-grid-height);
			position: relative;

			:global(*) {
				position: absolute;
				visibility: hidden;
			}
		}
	}
</style>
